import React, { useCallback, useEffect, useRef, useState } from "react";
import Quagga from "@ericblade/quagga2";
import styles from "../pages/admin/GastosPage.module.css";

type StatusKind = "ok" | "warn" | "err";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onScan: (code: string) => void;
};

const READERS = ["ean_reader", "ean_8_reader", "upc_reader", "upc_e_reader"];
const REQUIRED_EQUAL_READS = 3;
const CONFIRM_WINDOW_MS = 1800;
const MAX_AVG_DECODE_ERROR = 0.12;

const onlyDigits = (value: unknown) => String(value ?? "").replace(/\D/g, "");

const isValidGtinChecksum = (code: string) => {
  const digits = onlyDigits(code);
  if (![8, 12, 13, 14].includes(digits.length)) return false;
  const body = digits.slice(0, -1);
  let sum = 0;
  let weight = 3;
  for (let index = body.length - 1; index >= 0; index -= 1) {
    sum += Number(body[index]) * weight;
    weight = weight === 3 ? 1 : 3;
  }
  return (10 - (sum % 10)) % 10 === Number(digits.at(-1));
};

const validateResult = (result: any) => {
  const code = String(result?.codeResult?.code ?? "").trim();
  const digits = onlyDigits(code);
  if (!code || code !== digits || !isValidGtinChecksum(digits)) return null;

  const errors = (result?.codeResult?.decodedCodes ?? [])
    .map((part: any) => Number(part?.error))
    .filter(Number.isFinite);
  const averageError = errors.length
    ? errors.reduce((total: number, error: number) => total + error, 0) / errors.length
    : 0;
  return averageError <= MAX_AVG_DECODE_ERROR ? digits : null;
};

const BarcodeScannerModal: React.FC<Props> = ({ isOpen, onClose, onScan }) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const runningRef = useRef(false);
  const pendingRef = useRef({ code: "", count: 0, firstAt: 0 });
  const [status, setStatus] = useState("Preparando escáner…");
  const [statusKind, setStatusKind] = useState<StatusKind>("warn");
  const [tip, setTip] = useState("Abriendo cámara…");
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [cameraId, setCameraId] = useState("");
  const [facingMode, setFacingMode] = useState<"environment" | "user">("environment");
  const [running, setRunning] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [torchOn, setTorchOn] = useState(false);

  const getTrack = () => {
    const video = viewportRef.current?.querySelector("video");
    return (video?.srcObject as MediaStream | null)?.getVideoTracks?.()[0] ?? null;
  };

  const stopScanner = useCallback(() => {
    if (runningRef.current) {
      try { Quagga.stop(); } catch (_) { /* already stopped */ }
    }
    runningRef.current = false;
    setRunning(false);
    setTorchOn(false);
    setTorchSupported(false);
  }, []);

  const close = useCallback(() => {
    stopScanner();
    onClose();
  }, [onClose, stopScanner]);

  const acceptResult = useCallback((result: any, requireRepeatedReads = true) => {
    const code = validateResult(result);
    if (!code) return;
    const now = Date.now();
    const pending = pendingRef.current;
    if (!requireRepeatedReads || code !== pending.code || now - pending.firstAt > CONFIRM_WINDOW_MS) {
      pendingRef.current = { code, count: requireRepeatedReads ? 1 : REQUIRED_EQUAL_READS, firstAt: now };
    } else {
      pending.count += 1;
    }
    const count = pendingRef.current.count;
    setStatus(`Validando lectura ${count}/${REQUIRED_EQUAL_READS}: ${code}`);
    setTip("Mantén el código quieto dentro del recuadro.");
    if (count < REQUIRED_EQUAL_READS) return;
    setStatus(`Código confirmado: ${code}`);
    setStatusKind("ok");
    onScan(code);
    stopScanner();
    onClose();
  }, [onClose, onScan, stopScanner]);

  const refreshCameras = useCallback(async () => {
    try {
      const devices = (await navigator.mediaDevices.enumerateDevices()).filter(
        (device) => device.kind === "videoinput"
      );
      setCameras(devices);
      if (!cameraId && devices.length) {
        const rear = devices.find((device) => /back|rear|environment|trasera/i.test(device.label));
        setCameraId((rear ?? devices[0]).deviceId);
      }
    } catch (_) {
      setCameras([]);
    }
  }, [cameraId]);

  const startScanner = useCallback(async () => {
    if (!viewportRef.current || runningRef.current) return;
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      setStatus("La cámara requiere HTTPS o localhost y permiso del navegador.");
      setStatusKind("err");
      setTip("No se puede iniciar la cámara en este contexto.");
      return;
    }
    setStatus("Solicitando acceso a la cámara…");
    setStatusKind("warn");
    pendingRef.current = { code: "", count: 0, firstAt: 0 };

    const constraints = cameraId
      ? { deviceId: { exact: cameraId }, width: { ideal: 1280 }, height: { ideal: 720 } }
      : { facingMode, width: { ideal: 1280 }, height: { ideal: 720 } };

    Quagga.init({
      inputStream: {
        name: "Live",
        type: "LiveStream",
        target: viewportRef.current,
        constraints,
        area: { top: "18%", right: "7%", left: "7%", bottom: "18%" },
      },
      locate: true,
      locator: { halfSample: false, patchSize: "medium" },
      numOfWorkers: Math.min(4, Math.max(1, (navigator.hardwareConcurrency || 2) - 1)),
      frequency: 10,
      decoder: { readers: READERS },
    } as any, async (error: unknown) => {
      if (error) {
        setStatus(`No se pudo abrir la cámara: ${error instanceof Error ? error.message : String(error)}`);
        setStatusKind("err");
        return;
      }
      Quagga.start();
      runningRef.current = true;
      setRunning(true);
      setStatus("Cámara activa. Apunta al código de barras.");
      setTip("Coloca el código completo dentro del recuadro.");
      await refreshCameras();
      const capabilities = getTrack()?.getCapabilities?.() as MediaTrackCapabilities & { torch?: boolean };
      setTorchSupported(Boolean(capabilities?.torch));
    });
  }, [cameraId, facingMode, refreshCameras]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (result: any) => acceptResult(result);
    Quagga.onDetected(handler);
    refreshCameras();
    const onKeyDown = (event: KeyboardEvent) => event.key === "Escape" && close();
    document.addEventListener("keydown", onKeyDown);
    return () => {
      Quagga.offDetected(handler);
      document.removeEventListener("keydown", onKeyDown);
      stopScanner();
    };
  }, [acceptResult, close, isOpen, refreshCameras, stopScanner]);

  const changeCamera = async (nextCameraId: string) => {
    stopScanner();
    setCameraId(nextCameraId);
  };

  useEffect(() => {
    if (isOpen && !runningRef.current) startScanner();
  }, [cameraId, facingMode, isOpen, startScanner]);

  const toggleTorch = async () => {
    const track = getTrack();
    if (!track) return;
    const next = !torchOn;
    try {
      await track.applyConstraints({ advanced: [{ torch: next } as any] });
      setTorchOn(next);
    } catch (_) {
      setStatus("El dispositivo no permitió cambiar el flash.");
      setStatusKind("err");
    }
  };

  const scanImage = (file?: File) => {
    if (!file) return;
    setStatus("Analizando imagen…");
    setStatusKind("warn");
    const url = URL.createObjectURL(file);
    Quagga.decodeSingle({ src: url, locate: true, decoder: { readers: READERS } } as any, (result: any) => {
      URL.revokeObjectURL(url);
      if (validateResult(result)) acceptResult(result, false);
      else {
        setStatus("No se encontró un código EAN/UPC válido en la imagen.");
        setStatusKind("err");
      }
    });
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="scanner-title" onMouseDown={(e) => e.target === e.currentTarget && close()}>
      <div className={styles.modalCard}>
        <div className={styles.modalHead}>
          <h2 id="scanner-title">Escanear código de barras</h2>
          <button className={styles.danger} type="button" onClick={close}>Cerrar</button>
        </div>
        <div className={styles.modalBody}>
          <div ref={viewportRef} className={styles.scannerViewport}>
            <div className={styles.scanGuide} />
            <div className={styles.tip}>{tip}</div>
          </div>
          <div className={styles.stack}>
            <div className={`${styles.status} ${styles[statusKind]}`}>{status}</div>
            <div>
              <label className={styles.label} htmlFor="barcode-camera">Cámara detectada</label>
              <select id="barcode-camera" className={styles.select} value={cameraId} onChange={(e) => changeCamera(e.target.value)}>
                {!cameras.length && <option value="">Sin cámaras listadas todavía</option>}
                {cameras.map((camera, index) => <option key={camera.deviceId} value={camera.deviceId}>{camera.label || `Cámara ${index + 1}`}</option>)}
              </select>
            </div>
            <div className={styles.actions}>
              <button className={styles.primary} type="button" onClick={startScanner} disabled={running}>Iniciar cámara</button>
              <button className={styles.secondary} type="button" onClick={stopScanner} disabled={!running}>Detener</button>
              <button className={styles.secondary} type="button" onClick={() => { stopScanner(); setCameraId(""); setFacingMode((value) => value === "environment" ? "user" : "environment"); }}>Cambiar cámara</button>
            </div>
            <div className={styles.actions}>
              <button className={styles.secondary} type="button" onClick={toggleTorch} disabled={!torchSupported}>{torchOn ? "Apagar flash" : "Flash"}</button>
              <label className={`${styles.fileButton} ${styles.secondary}`}>
                Escanear imagen
                <input type="file" accept="image/*" capture="environment" onChange={(e) => scanImage(e.target.files?.[0])} />
              </label>
            </div>
            <p className={styles.help}>Lee EAN-13, EAN-8, UPC-A y UPC-E. Para mejores resultados usa buena luz y coloca el código completo dentro del recuadro.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BarcodeScannerModal;
